import { useEffect } from "react";
import { FormInstance } from "@ir/ui";
import { FilterInfo } from "@/store/deduction/deductionFilter";
import { FilterDateRange } from "../interfaces";
import { ApprovalFilterInfo } from "@/store/approvals";
import { AnalyticsFilterInfo } from "@/store/analytics";
import { AccountingRemittanceFilterInfo } from "@/store/accounting";

interface UseFormSyncProps {
  form: FormInstance;
  filterInfo: FilterInfo | ApprovalFilterInfo | AnalyticsFilterInfo | AccountingRemittanceFilterInfo;
  defaultFilterInfo: FilterInfo | ApprovalFilterInfo | AnalyticsFilterInfo | AccountingRemittanceFilterInfo;
  isFilterApplied: boolean;
  calculateDateRange: (value: FilterDateRange | null, fieldName: "checkDateRange" | "invoiceDateRange") => { fromDate: string | null; toDate: string | null };
}

function resolveCheckDateField(
  effectiveCheckDateRange: FilterDateRange | null,
  formattedDate: string | null,
  storedDate: string | null | undefined,
  defaultDate: string | null | undefined,
  isFilterApplied: boolean,
): string | null | undefined {
  if (effectiveCheckDateRange) {
    return formattedDate;
  }
  if (isFilterApplied) {
    return storedDate;
  }
  return storedDate ?? formattedDate ?? defaultDate;
}

function resolveInvoiceDateField(
  effectiveInvoiceDateRange: FilterDateRange | null,
  formattedDate: string | null,
  storedDate: string | null | undefined,
): string | null {
  if (effectiveInvoiceDateRange) {
    return formattedDate;
  }
  return storedDate ?? formattedDate;
}

/**
 * Synchronizes form values with filterInfo state
 * Only uses default checkDateRange when filter is not applied (initial state)
 */
export const useFormSync = ({
  form,
  filterInfo,
  defaultFilterInfo,
  isFilterApplied,
  calculateDateRange,
}: UseFormSyncProps) => {
  useEffect(() => {
    // Only use default if filter is not applied (initial state)
    // If filter is applied and checkDateRange is null, respect that null
    const effectiveCheckDateRange = isFilterApplied
      ? (filterInfo.checkDateRange as FilterDateRange | null)
      : ((filterInfo.checkDateRange ?? defaultFilterInfo.checkDateRange) as FilterDateRange | null);
    
    const { fromDate: checkFromDateFormatted, toDate: checkToDateFormatted } = effectiveCheckDateRange
      ? calculateDateRange(effectiveCheckDateRange, "checkDateRange")
      : { fromDate: null, toDate: null };
    
    const effectiveInvoiceDateRange = (
      "invoiceDateRange" in filterInfo ? filterInfo.invoiceDateRange : null
    ) as FilterDateRange | null;
    const { fromDate: invoiceFromDateFormatted, toDate: invoiceToDateFormatted } = effectiveInvoiceDateRange
      ? calculateDateRange(effectiveInvoiceDateRange, "invoiceDateRange")
      : { fromDate: null, toDate: null };

    const checkFromDate = resolveCheckDateField(
      effectiveCheckDateRange,
      checkFromDateFormatted,
      filterInfo.checkFromDate,
      defaultFilterInfo.checkFromDate,
      isFilterApplied,
    );
    const checkToDate = resolveCheckDateField(
      effectiveCheckDateRange,
      checkToDateFormatted,
      filterInfo.checkToDate,
      defaultFilterInfo.checkToDate,
      isFilterApplied,
    );
    const invoiceFromDate = resolveInvoiceDateField(
      effectiveInvoiceDateRange,
      invoiceFromDateFormatted,
      "invoiceFromDate" in filterInfo ? filterInfo.invoiceFromDate : null,
    );
    const invoiceToDate = resolveInvoiceDateField(
      effectiveInvoiceDateRange,
      invoiceToDateFormatted,
      "invoiceToDate" in filterInfo ? filterInfo.invoiceToDate : null,
    );

    const filterFormInitialValues: Record<string, unknown> = {
      checkDateRange: effectiveCheckDateRange,
      invoiceDateRange: "invoiceDateRange" in filterInfo ? filterInfo.invoiceDateRange : null,
      checkFromDate,
      checkToDate,
      invoiceFromDate,
      invoiceToDate,
      purchaser: filterInfo.purchaser,
      dc: (filterInfo as FilterInfo | ApprovalFilterInfo)?.dc ?? null,
      priority: (filterInfo as FilterInfo)?.priority ?? null,
      validity: (filterInfo as FilterInfo)?.validity ?? null,
      adjustmentTypes: (filterInfo as FilterInfo)?.adjustmentTypes ?? null,
      adjustmentCategories: (filterInfo as FilterInfo)?.adjustmentCategories ?? null,
      deductionTypeCategories: (filterInfo as FilterInfo)?.deductionTypeCategories ?? null,
      poNumberSearch: (filterInfo as FilterInfo)?.poNumberSearch ?? null,
      retailer: (filterInfo as FilterInfo)?.retailer ?? null,
      glCode: (filterInfo as ApprovalFilterInfo)?.glCode ?? null,
      analyst: (filterInfo as ApprovalFilterInfo)?.analyst ?? null,
      approver: (filterInfo as ApprovalFilterInfo)?.approver ?? null,
      progress: (filterInfo as FilterInfo)?.progress ?? null,
    };
    
    form.setFieldsValue(filterFormInitialValues);
  }, [filterInfo, defaultFilterInfo, form, calculateDateRange, isFilterApplied]);
};
